import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart3, Table, AlertCircle, RefreshCw,
    Building2, ChevronDown, ChevronUp
} from 'lucide-react';
import { API_BASE } from './helpers';

// ─── Yardi T12 Parser ─────────────────────────────────────────────
// Template contract:
//   Column A = category label, Column B = amount
//   Row with "TOTAL INCOME" → revenue
//   Row with "NET INCOME"   → calculated result (KPI only, not in chart)
//   All other rows with numeric B → expense categories
function parseT12(headers, rows) {
    // BUG-09: "returns null when it cannot parse" is this parser's contract, so
    // it has to hold for a malformed payload too — a non-array headers/rows
    // would otherwise throw out of `for…of` before any render guard could run.
    if (!Array.isArray(headers) || !Array.isArray(rows)) return null;

    let income = null;
    let netIncome = null;
    let period = null;
    const expenses = [];

    // Check headers for period info
    for (const h of headers) {
        const p = extractPeriod(h);
        if (p) { period = p; break; }
    }

    for (const row of rows) {
        if (!Array.isArray(row)) continue;   // BUG-09: same reason as above
        // Check every cell for period info if not found yet
        if (!period) {
            for (const cell of row) {
                const p = extractPeriod(cell);
                if (p) { period = p; break; }
            }
        }

        const label = row[0] != null ? String(row[0]).trim() : '';
        const value = parseNumber(row[1]);
        if (!label || value === null) continue;

        const upperLabel = label.toUpperCase();

        if (upperLabel.includes('TOTAL INCOME')) {
            income = { label: cleanLabel(label), value };
        } else if (upperLabel.includes('NET INCOME')) {
            netIncome = { label: cleanLabel(label), value };
        } else {
            expenses.push({ label: cleanLabel(label), value });
        }
    }

    if (!income) return null;

    const totalExpenses = expenses.reduce((s, e) => s + e.value, 0);

    return { income, expenses, netIncome, totalExpenses, period };
}

// ─── Utilities ────────────────────────────────────────────────────
function parseNumber(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number') return val;
    const str = String(val).replace(/[$,%\s]/g, '').replace(/\((.+)\)/, '-$1');
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
}

function cleanLabel(label) {
    return label.replace(/^TOTAL\s+/i, '').trim();
}

function extractPeriod(val) {
    if (val == null) return null;
    const str = String(val).trim();
    // Match patterns like "Period = Jan 2026" or "Period: January 2026" or just "Jan 2026"
    const match = str.match(/(?:period\s*[=:]\s*)?([A-Za-z]+\.?\s+\d{4})/i);
    if (match) return match[1].trim();
    return null;
}

function formatCurrency(val) {
    if (val == null) return '—';
    const abs = Math.abs(val);
    const formatted = '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return val < 0 ? '−' + formatted : formatted;
}

// ─── Color palette for expense segments ───────────────────────────
const EXPENSE_COLORS = ['#dc2626', '#ea580c', '#d97706', '#7c3aed', '#0e7490', '#be185d', '#4338ca', '#166534'];
const INCOME_COLOR = '#059669';

// ─── Waterfall Chart ──────────────────────────────────────────────
function WaterfallChart({ data }) {
    // BUG-09: the chart is built entirely out of income + expenses, and there
    // is no useful degraded form of it — without an income total there is no
    // baseline to walk down from. So instead of drawing a broken axis, fall
    // back to the component's existing "no data" line. parseT12 guarantees
    // `income` today (it returns null outright when TOTAL INCOME is missing),
    // and aggregateT12 always constructs one; this is the guard for the
    // aggregate/template drifting, and for `data` arriving null at all.
    const income = data?.income;
    const expenses = Array.isArray(data?.expenses) ? data.expenses : [];
    if (!income || income.value == null) {
        return (
            <p className="investor-dashboard__no-data">
                Chart unavailable — no income total in this spreadsheet.
            </p>
        );
    }

    const steps = [
        { label: income.label, value: income.value, type: 'income' },
        ...expenses.map((e, i) => ({ label: e.label, value: e.value, type: 'expense', colorIndex: i }))
    ];

    // Compute running totals for waterfall positioning
    let running = 0;
    const bars = steps.map(step => {
        const start = running;
        if (step.type === 'income') {
            running += step.value;
            return { ...step, top: 0, bottom: step.value, barStart: 0, barEnd: step.value };
        } else {
            // Expense: subtract from running total
            const newRunning = running - step.value;
            const bar = { ...step, barStart: newRunning, barEnd: running };
            running = newRunning;
            return bar;
        }
    });

    // Find the value range for scaling
    const allValues = bars.flatMap(b => [b.barStart, b.barEnd]);
    const minVal = Math.min(0, ...allValues);
    const maxVal = Math.max(0, ...allValues);
    const range = maxVal - minVal || 1;

    // Chart dimensions
    const barCount = bars.length;
    const barWidth = Math.min(80, Math.max(50, 480 / barCount));
    const barGap = Math.min(20, Math.max(8, 100 / barCount));
    const padding = { top: 30, right: 30, bottom: 90, left: 85 };
    const plotW = barCount * (barWidth + barGap) - barGap;
    const chartWidth = padding.left + plotW + padding.right;
    const chartHeight = 330;
    const plotH = chartHeight - padding.top - padding.bottom;

    // Convert value to Y position
    const valToY = (v) => padding.top + plotH - ((v - minVal) / range) * plotH;
    const zeroY = valToY(0);

    // Y-axis ticks
    const tickCount = 5;
    const tickStep = range / tickCount;
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => minVal + tickStep * i);

    return (
        <div className="investor-chart__container">
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="investor-chart__svg" preserveAspectRatio="xMidYMid meet">
                {/* Grid lines */}
                {ticks.map((tick, i) => {
                    const y = valToY(tick);
                    return (
                        <g key={i}>
                            <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y}
                                stroke={tick === 0 ? '#44403c' : '#e5e2de'}
                                strokeWidth={tick === 0 ? 1.5 : 1}
                                strokeDasharray={tick === 0 ? '' : '4,4'} />
                            <text x={padding.left - 10} y={y + 4} textAnchor="end"
                                fill="#78716c" fontSize="11" fontFamily="inherit">
                                {formatCurrency(tick)}
                            </text>
                        </g>
                    );
                })}

                {/* Zero line if not already a tick */}
                {!ticks.some(t => Math.abs(t) < tickStep * 0.1) && (
                    <line x1={padding.left} y1={zeroY} x2={chartWidth - padding.right} y2={zeroY}
                        stroke="#44403c" strokeWidth="1.5" />
                )}

                {/* Bars and connectors */}
                {bars.map((bar, i) => {
                    const x = padding.left + i * (barWidth + barGap);
                    const yTop = valToY(Math.max(bar.barStart, bar.barEnd));
                    const yBottom = valToY(Math.min(bar.barStart, bar.barEnd));
                    const barH = Math.max(2, yBottom - yTop);
                    const color = bar.type === 'income' ? INCOME_COLOR : EXPENSE_COLORS[bar.colorIndex % EXPENSE_COLORS.length];

                    // Connector line to next bar
                    const nextX = padding.left + (i + 1) * (barWidth + barGap);
                    const connectorY = valToY(bar.type === 'income' ? bar.barEnd : bar.barStart);

                    return (
                        <g key={i}>
                            <rect x={x} y={yTop} width={barWidth} height={barH}
                                rx="3" fill={color} opacity="0.85"
                                className="investor-chart__bar" />

                            {/* Value label above/below bar */}
                            <text x={x + barWidth / 2} y={bar.type === 'income' ? yTop - 8 : yBottom + 14}
                                textAnchor="middle" fill={color} fontSize="10.5" fontWeight="700" fontFamily="inherit">
                                {bar.type === 'expense' ? '−' : ''}{formatCurrency(bar.value)}
                            </text>

                            {/* X-axis label */}
                            <text x={x + barWidth / 2} y={chartHeight - padding.bottom + 24}
                                textAnchor="end" fill="#57534e" fontSize="10" fontFamily="inherit"
                                transform={`rotate(-30, ${x + barWidth / 2}, ${chartHeight - padding.bottom + 24})`}>
                                {bar.label.length > 16 ? bar.label.slice(0, 15) + '…' : bar.label}
                            </text>

                            {/* Connector line to next bar */}
                            {i < bars.length - 1 && (
                                <line x1={x + barWidth} y1={connectorY} x2={nextX} y2={connectorY}
                                    stroke="#a8a29e" strokeWidth="1" strokeDasharray="3,3" />
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Legend */}
            <div className="investor-chart__legend">
                <div className="investor-chart__legend-item">
                    <span className="investor-chart__legend-dot" style={{ background: INCOME_COLOR }} />
                    <span>{income.label}</span>
                </div>
                {expenses.map((e, i) => (
                    <div key={i} className="investor-chart__legend-item">
                        <span className="investor-chart__legend-dot" style={{ background: EXPENSE_COLORS[i % EXPENSE_COLORS.length] }} />
                        <span>{e.label} ({formatCurrency(e.value)})</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── KPI Cards ────────────────────────────────────────────────────
function T12KPICards({ data }) {
    // BUG-09. `data.netIncome.value` was the crash: parseT12 leaves netIncome
    // null whenever the sheet has no NET INCOME row, and one such spreadsheet
    // white-screened the whole portal. income/totalExpenses are guaranteed by
    // parseT12's own contract (it returns null unless it found TOTAL INCOME)
    // and by aggregateT12, but they are read the same null-safe way so a
    // future template change cannot reopen this from a different row.
    //
    // formatCurrency(null) already renders '—', which is exactly this
    // component's existing empty-value style, so an absent row degrades to a
    // dash rather than to a missing card. When Net Income is absent there is
    // no sign to colour by, so the inline colour is dropped and the value
    // inherits .investor-kpi__value's var(--text-primary).
    const incomeValue = data?.income?.value ?? null;
    const totalExpenses = data?.totalExpenses ?? null;
    const netIncomeValue = data?.netIncome?.value ?? null;
    const netIncomeColor = netIncomeValue == null
        ? undefined
        : (netIncomeValue >= 0 ? INCOME_COLOR : '#dc2626');

    return (
        <div className="investor-dashboard__kpis">
            <div className="investor-kpi">
                <div className="investor-kpi__label">Total Income</div>
                <div className="investor-kpi__value" style={{ color: INCOME_COLOR }}>{formatCurrency(incomeValue)}</div>
            </div>
            <div className="investor-kpi">
                <div className="investor-kpi__label">Total Expenses</div>
                <div className="investor-kpi__value" style={{ color: '#dc2626' }}>{formatCurrency(totalExpenses)}</div>
            </div>
            <div className="investor-kpi">
                <div className="investor-kpi__label">Net Income</div>
                <div className="investor-kpi__value" style={{ color: netIncomeColor }}>
                    {formatCurrency(netIncomeValue)}
                </div>
            </div>
        </div>
    );
}

// ─── Data Table ───────────────────────────────────────────────────
function DataTable({ headers, rows }) {
    const [page, setPage] = useState(0);
    const pageSize = 10;
    const totalPages = Math.ceil(rows.length / pageSize);
    const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="investor-table-wrap">
            <div className="investor-table__scroll">
                <table className="investor-table">
                    <thead>
                        <tr>
                            {headers.map((h, i) => <th key={i}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row, ri) => (
                            <tr key={ri}>
                                {row.map((cell, ci) => <td key={ci}>{cell != null ? String(cell) : ''}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="investor-table__pagination">
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span>Page {page + 1} of {totalPages}</span>
                    <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            )}
        </div>
    );
}

// ─── Per-Property Section ─────────────────────────────────────────
function PropertySection({ property, t12Data }) {
    const [expanded, setExpanded] = useState(false);
    const [showTable, setShowTable] = useState(false);

    // BUG-09: same class of crash as the KPI card, one level up — the row/
    // column counter and DataTable both dereference these unconditionally, so
    // a property row that arrives without them took the portal down. An empty
    // sheet reads as "0 rows × 0 columns" and an empty table.
    const rows = Array.isArray(property.rows) ? property.rows : [];
    const headers = Array.isArray(property.headers) ? property.headers : [];

    return (
        <div className="investor-dashboard__property">
            <button className="investor-dashboard__property-header" onClick={() => setExpanded(!expanded)}>
                <div className="investor-dashboard__property-info">
                    <Building2 size={16} />
                    <span className="investor-dashboard__property-name">{property.propertyName}</span>
                    {property.city && property.state && (
                        <span className="investor-dashboard__property-location">{property.city}, {property.state}</span>
                    )}
                    {t12Data?.period && (
                        <span className="investor-dashboard__property-period">{t12Data.period}</span>
                    )}
                </div>
                <div className="investor-dashboard__property-toggle">
                    <span className="investor-dashboard__property-file">{property.fileName}</span>
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </button>

            {expanded && (
                <div className="investor-dashboard__property-body">
                    {t12Data ? (
                        <>
                            <T12KPICards data={t12Data} />
                            <div style={{ marginTop: '1rem' }}>
                                <WaterfallChart data={t12Data} />
                            </div>
                        </>
                    ) : (
                        <p className="investor-dashboard__no-data">Could not parse T12 data from this spreadsheet.</p>
                    )}

                    {/* Table toggle */}
                    <div className="investor-dashboard__table-toggle">
                        <button
                            className={`investor-dashboard__view-btn ${showTable ? 'investor-dashboard__view-btn--active' : ''}`}
                            onClick={() => setShowTable(!showTable)}
                        >
                            <Table size={14} />
                            {showTable ? 'Hide Data' : 'View Data'}
                        </button>
                        <span className="investor-dashboard__row-count">
                            {rows.length} rows × {headers.length} columns
                        </span>
                    </div>

                    {showTable && (
                        <DataTable headers={headers} rows={rows} />
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Aggregate T12 data across multiple properties ────────────────
// Month abbreviation map for sorting
const MONTH_ORDER = { jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11 };

function parsePeriodDate(periodStr) {
    if (!periodStr) return null;
    const parts = periodStr.trim().split(/\s+/);
    if (parts.length < 2) return null;
    const monthKey = parts[0].toLowerCase().replace(/\.$/, '');
    const year = parseInt(parts[parts.length - 1], 10);
    const month = MONTH_ORDER[monthKey];
    if (month == null || isNaN(year)) return null;
    return { month, year, original: periodStr };
}

function buildPeriodLabel(allT12) {
    const periods = allT12
        .filter(d => d !== null && d.period)
        .map(d => parsePeriodDate(d.period))
        .filter(p => p !== null);

    if (periods.length === 0) return null;

    // Sort by year then month
    periods.sort((a, b) => a.year - b.year || a.month - b.month);

    const earliest = periods[0];
    const latest = periods[periods.length - 1];

    // If all same period, return single
    if (earliest.month === latest.month && earliest.year === latest.year) {
        return earliest.original;
    }

    // Build range string
    return `${earliest.original}–${latest.original}`;
}

function aggregateT12(allT12) {
    const valid = allT12.filter(d => d !== null);
    if (valid.length === 0) return null;

    const totalIncome = valid.reduce((s, d) => s + d.income.value, 0);
    const totalExpensesSum = valid.reduce((s, d) => s + d.totalExpenses, 0);
    const totalNetIncome = valid.reduce((s, d) => s + (d.netIncome ? d.netIncome.value : 0), 0);

    // Build period label from all properties
    const period = buildPeriodLabel(valid);

    // Merge expense categories across properties
    const expenseMap = {};
    for (const d of valid) {
        for (const e of d.expenses) {
            const key = e.label.toUpperCase();
            if (!expenseMap[key]) expenseMap[key] = { label: e.label, value: 0 };
            expenseMap[key].value += e.value;
        }
    }

    return {
        income: { label: 'Income', value: totalIncome },
        expenses: Object.values(expenseMap),
        netIncome: { label: 'Net Income', value: totalNetIncome },
        totalExpenses: totalExpensesSum,
        period
    };
}

// ─── Main Dashboard Widget ────────────────────────────────────────
export default function InvestorDataDashboard({ authHeaders, onAuthExpired }) {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchDashboardData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API_BASE}/investor/dashboard-data`, authHeaders);
            setDashboardData(res.data);
        } catch (err) {
            // BUG-19: a 401 here used to be discarded, so a deactivated or
            // expired investor would sit in front of a silently frozen widget.
            // It now defers to the portal's own sign-out — the same
            // handleLogout the portal's other two 401 paths call — rather than
            // owning a second, divergent notion of what an expired session
            // means. Every other error is unchanged and still renders the
            // widget's inline error + Retry below.
            if (err.response?.status === 401) {
                if (onAuthExpired) onAuthExpired();
                return;
            }
            setError(err.response?.data?.error || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Parse T12 data for each property
    const propertyT12s = useMemo(() => {
        if (!dashboardData?.properties) return [];
        return dashboardData.properties.map(prop => ({
            property: prop,
            t12Data: parseT12(prop.headers, prop.rows)
        }));
    }, [dashboardData]);

    // Aggregate across all properties
    const aggregated = useMemo(() => {
        return aggregateT12(propertyT12s.map(p => p.t12Data));
    }, [propertyT12s]);

    // Don't render if no data
    if (!loading && (!dashboardData?.properties || dashboardData.properties.length === 0)) {
        return null;
    }

    return (
        <div className="investor-portal__card investor-dashboard-widget">
            <div className="investor-portal__card-header">
                <div className="investor-portal__card-title">
                    <BarChart3 size={20} />
                    <h2>Investment Dashboard</h2>
                </div>
                {dashboardData?.properties && (
                    <span className="investor-portal__card-count">
                        {dashboardData.properties.length} propert{dashboardData.properties.length !== 1 ? 'ies' : 'y'}
                    </span>
                )}
            </div>

            <div className="investor-portal__card-body">
                {loading ? (
                    <div className="investor-portal__loading">
                        <div className="crm-loading__spinner" />
                        <span>Loading dashboard data...</span>
                    </div>
                ) : error ? (
                    <div className="investor-dashboard__error">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                        <button onClick={fetchDashboardData} className="investor-dashboard__retry">
                            <RefreshCw size={14} /> Retry
                        </button>
                    </div>
                ) : (
                    <div className="investor-dashboard__content">
                        {/* Portfolio Summary */}
                        {aggregated && (
                            <div className="investor-dashboard__aggregate">
                                <div className="investor-dashboard__section-label">
                                    Portfolio Summary{aggregated.period ? ` (${aggregated.period})` : ''}
                                </div>
                                <T12KPICards data={aggregated} />
                                <div style={{ marginTop: '1rem' }}>
                                    <WaterfallChart data={aggregated} />
                                </div>
                            </div>
                        )}

                        {/* Per-Property Breakdown */}
                        <div className="investor-dashboard__properties">
                            <div className="investor-dashboard__section-label">By Property</div>
                            {propertyT12s.map(({ property, t12Data }) => (
                                <PropertySection key={property.propertyId} property={property} t12Data={t12Data} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
